"use client";

import { useMemo, useState } from "react";
import { Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { ClearButton } from "@/components/tools/clear-button";
import { CopyButton } from "@/components/tools/copy-button";
import { ErrorMessage } from "@/components/tools/error-message";
import { InputOutputPanel } from "@/components/tools/input-output-panel";
import { CodeOutput } from "@/components/tools/tool-output";
import { convertCase } from "@/lib/tools/case";
import { diffLines } from "@/lib/tools/diff";
import { csvToMarkdownTable } from "@/lib/tools/markdown";
import { generateSlug, type SlugSeparator } from "@/lib/tools/slug";
import { calculateTextStatistics, type TextStatistics } from "@/lib/tools/text-stats";
import { cn } from "@/lib/utils";

export function TextDiffTool() {
  const [left, setLeft] = useState("");
  const [right, setRight] = useState("");
  const diff = useMemo(() => diffLines(left, right), [left, right]);
  const output = diff
    .map((line) => `${line.type === "added" ? "+" : line.type === "removed" ? "-" : " "} ${line.value}`)
    .join("\n");

  return (
    <InputOutputPanel
      inputTitle="Compare text"
      outputTitle="Line diff"
      inputActions={
        <>
          <Button
            type="button"
            variant="secondary"
            size="sm"
            onClick={() => {
              setLeft("Launch LocalTools\nFormat JSON locally\nPreview metadata");
              setRight("Launch LocalTools\nFormat JSON privately\nPreview metadata\nShip MVP");
            }}
          >
            <Sparkles className="h-4 w-4" />
            Example
          </Button>
          <ClearButton
            onClear={() => {
              setLeft("");
              setRight("");
            }}
          />
        </>
      }
      outputActions={<CopyButton value={output} />}
      input={
        <div className="grid gap-3 md:grid-cols-2">
          <Textarea
            value={left}
            onChange={(event) => setLeft(event.target.value)}
            placeholder="Original text..."
            className="min-h-80"
          />
          <Textarea
            value={right}
            onChange={(event) => setRight(event.target.value)}
            placeholder="Changed text..."
            className="min-h-80"
          />
        </div>
      }
      output={
        left || right ? (
          <div className="max-h-[34rem] overflow-auto rounded-md border border-zinc-200 bg-white font-mono text-sm">
            {diff.map((line, index) => (
              <div
                key={`${line.lineNumber}-${index}-${line.type}`}
                className={cn(
                  "grid grid-cols-[3rem_1fr] gap-3 border-b border-zinc-100 px-3 py-1.5 last:border-0",
                  line.type === "added" && "bg-emerald-50 text-emerald-900",
                  line.type === "removed" && "bg-red-50 text-red-900",
                  line.type === "unchanged" && "text-zinc-600",
                )}
              >
                <span className="select-none text-zinc-400">{line.lineNumber}</span>
                <span className="whitespace-pre-wrap break-words">
                  {line.type === "added" ? "+ " : line.type === "removed" ? "- " : "  "}
                  {line.value}
                </span>
              </div>
            ))}
          </div>
        ) : (
          <CodeOutput placeholder="Line differences will appear here." />
        )
      }
    />
  );
}

const textStatisticsExample = `Launch checklist

Format customer CSV exports.
Review release notes, then publish the docs.`;

function formatStatisticLabel(key: keyof TextStatistics) {
  const labels: Record<keyof TextStatistics, string> = {
    characters: "Characters",
    charactersNoSpaces: "Characters without spaces",
    words: "Words",
    uniqueWords: "Unique words",
    lines: "Lines",
    nonEmptyLines: "Non-empty lines",
    paragraphs: "Paragraphs",
    sentences: "Sentences",
    bytes: "Bytes",
    readingTimeMinutes: "Reading time",
    averageWordLength: "Average word length",
    longestLineLength: "Longest line",
  };

  return labels[key];
}

function formatStatisticValue(key: keyof TextStatistics, value: number) {
  if (key === "readingTimeMinutes") {
    return value === 1 ? "1 min" : `${value} mins`;
  }

  if (key === "averageWordLength" || key === "longestLineLength") {
    return `${value} chars`;
  }

  return value.toLocaleString();
}

export function TextStatisticsTool() {
  const [input, setInput] = useState("");
  const statistics = useMemo(() => calculateTextStatistics(input), [input]);
  const statisticRows: Array<keyof TextStatistics> = [
    "words",
    "uniqueWords",
    "characters",
    "charactersNoSpaces",
    "lines",
    "nonEmptyLines",
    "paragraphs",
    "sentences",
    "readingTimeMinutes",
    "averageWordLength",
    "longestLineLength",
    "bytes",
  ];
  const copyValue = statisticRows
    .map((key) => `${formatStatisticLabel(key)}: ${formatStatisticValue(key, statistics[key])}`)
    .join("\n");

  return (
    <InputOutputPanel
      inputTitle="Text for statistics"
      outputTitle="Statistics"
      inputActions={
        <>
          <Button
            type="button"
            variant="secondary"
            size="sm"
            onClick={() => setInput(textStatisticsExample)}
          >
            <Sparkles className="h-4 w-4" />
            Example
          </Button>
          <ClearButton onClear={() => setInput("")} />
        </>
      }
      outputActions={<CopyButton value={input ? copyValue : ""} label="Copy stats" />}
      input={
        <Textarea
          value={input}
          onChange={(event) => setInput(event.target.value)}
          placeholder="Paste text to count words, lines, paragraphs, and reading time..."
          className="min-h-80"
        />
      }
      output={
        input ? (
          <div className="overflow-hidden rounded-md border border-zinc-200">
            <table className="w-full text-left text-sm">
              <tbody>
                {statisticRows.map((key) => (
                  <tr key={key} className="border-b border-zinc-100 last:border-0">
                    <th className="w-56 bg-zinc-50 px-3 py-3 font-medium text-zinc-600">
                      {formatStatisticLabel(key)}
                    </th>
                    <td className="px-3 py-3 font-mono text-zinc-950">
                      {formatStatisticValue(key, statistics[key])}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <CodeOutput placeholder="Text metrics will appear here." />
        )
      }
    />
  );
}

export function SlugGeneratorTool() {
  const [input, setInput] = useState("");
  const [separator, setSeparator] = useState<SlugSeparator>("-");
  const slug = useMemo(() => generateSlug(input, separator), [input, separator]);

  return (
    <InputOutputPanel
      inputTitle="Text to slugify"
      outputTitle="Slug"
      inputActions={
        <>
          <Button
            type="button"
            variant="secondary"
            size="sm"
            onClick={() => setInput("Crème brûlée launch notes for makers")}
          >
            <Sparkles className="h-4 w-4" />
            Example
          </Button>
          <ClearButton onClear={() => setInput("")} />
        </>
      }
      outputActions={<CopyButton value={slug} />}
      input={
        <div className="space-y-4">
          <Textarea
            value={input}
            onChange={(event) => setInput(event.target.value)}
            placeholder="Enter title or text..."
            className="min-h-64"
          />
          <div className="max-w-xs space-y-2">
            <Label htmlFor="separator">Separator</Label>
            <Select
              id="separator"
              value={separator}
              onChange={(event) => setSeparator(event.target.value as SlugSeparator)}
            >
              <option value="-">Hyphen</option>
              <option value="_">Underscore</option>
            </Select>
          </div>
        </div>
      }
      output={<CodeOutput value={slug} placeholder="Your URL-safe slug will appear here." />}
    />
  );
}

export function CaseConverterTool() {
  const [input, setInput] = useState("");
  const outputs = useMemo(() => convertCase(input), [input]);
  const copyValue = outputs.map((item) => `${item.label}: ${item.value}`).join("\n");

  return (
    <InputOutputPanel
      inputTitle="Text for case conversion"
      outputTitle="Converted cases"
      inputActions={
        <>
          <Button
            type="button"
            variant="secondary"
            size="sm"
            onClick={() => setInput("privacy first developer toolbox")}
          >
            <Sparkles className="h-4 w-4" />
            Example
          </Button>
          <ClearButton onClear={() => setInput("")} />
        </>
      }
      outputActions={<CopyButton value={copyValue} label="Copy all" />}
      input={
        <Textarea
          value={input}
          onChange={(event) => setInput(event.target.value)}
          placeholder="Enter text to convert..."
          className="min-h-72"
        />
      }
      output={
        input ? (
          <div className="overflow-hidden rounded-md border border-zinc-200">
            <table className="w-full text-left text-sm">
              <tbody>
                {outputs.map((item) => (
                  <tr key={item.format} className="border-b border-zinc-100 last:border-0">
                    <th className="w-40 bg-zinc-50 px-3 py-3 font-medium text-zinc-600">
                      {item.label}
                    </th>
                    <td className="break-all px-3 py-3 font-mono text-zinc-950">{item.value}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <CodeOutput placeholder="Converted case variants will appear here." />
        )
      }
    />
  );
}


export function CsvToMarkdownTool() {
  const [input, setInput] = useState("");
  const [output, setOutput] = useState("");
  const [error, setError] = useState("");

  const process = () => {
    const result = csvToMarkdownTable(input);
    setOutput(result.ok ? result.value : "");
    setError(result.ok ? "" : result.error);
  };

  return (
    <InputOutputPanel
      inputTitle="CSV"
      outputTitle="Markdown table"
      inputActions={
        <>
          <Button
            type="button"
            variant="secondary"
            size="sm"
            onClick={() => setInput("Tool,Category,Local Only\nJSON Formatter,Developer,Yes\nUTM Builder,Web/SEO,Yes")}
          >
            <Sparkles className="h-4 w-4" />
            Example
          </Button>
          <ClearButton
            onClear={() => {
              setInput("");
              setOutput("");
              setError("");
            }}
          />
        </>
      }
      outputActions={<CopyButton value={output} />}
      input={
        <div className="space-y-3">
          <Textarea
            value={input}
            onChange={(event) => setInput(event.target.value)}
            placeholder="name,price,stock"
            className="min-h-72"
          />
          <ErrorMessage message={error} />
          <Button type="button" onClick={process}>Generate markdown table</Button>
        </div>
      }
      output={<CodeOutput value={output} placeholder="Markdown output will appear here." />}
    />
  );
}
